import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ButtonHoverAnimation from "./button/ButtonHoverAnimation";
import { useAccountInfo } from "../utils/constants";
import { PepecoinBalance, usePepecoinBalance } from "./balances";
import {
  useWriteContract,
  useReadContract,
  useAccount,
  useWaitForTransactionReceipt,
} from "wagmi";
import { brain721Abi } from "../utils/brain721Abi";
import { parseEther, formatUnits, parseAbi } from "viem";
import StakingSuccess from "./stakingsuccess";
import { useSpring, animated } from "react-spring";

const brainCA = process.env.REACT_APP_BRAIN_ERC721_CA;
const pepecoinCA = process.env.REACT_APP_PEPECOIN_CONTRACT;

const pepecoinABI = parseAbi([
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
]);

const Staking = () => {
  const [pepecoinToStake, setPepecoinToStake] = useState(0);
  const { shortenedAddress } = useAccountInfo();
  const [prevPepecoins, setPrevPepecoins] = useState(pepecoinToStake);
  const pepecoinBalance = usePepecoinBalance();
  const account = useAccount();

  const {
    data: approvalHash,
    error: approvalError,
    isPending: isApprovalPending,
    writeContract: writeApprovalContract,
  } = useWriteContract();

  const { number } = useSpring({
    number: pepecoinToStake,
    from: { number: prevPepecoins },
    reset: false,
    // onRest: () => setPrevPepecoins(pepecoinToStake),
  });

  const {
    data: stakeHash,
    error: stakeError,
    isPending: isStakePending,
    writeContract: writeStakeContract,
  } = useWriteContract();

  const { isLoading: isApprovalConfirming, isSuccess: isApprovalConfirmed } =
    useWaitForTransactionReceipt({
      hash: approvalHash,
    });

  const { isLoading: isStakeConfirming, isSuccess: isStakeConfirmed } =
    useWaitForTransactionReceipt({
      hash: stakeHash,
    });

  const pepecoinToStakeUint256 = parseEther(pepecoinToStake.toString());

  // Helper function to format numbers into short form
  const formatNumber = (num) => {
    num = Number(num); // Ensure num is treated as a number
    if (num >= 1000000) return (num / 1000000).toFixed(0) + "M";
    if (num >= 1000) return (num / 1000).toFixed(0) + "k"; // Corrected from 100000 to 1000
    return num.toString();
  };

  const { data: stakedAmount } = useReadContract({
    address: brainCA,
    abi: brain721Abi,
    functionName: "getStakedAmount",
    args: [account.address],
    query: {
      refetchInterval: 3000,
    },
  });

  // Convert stakedAmount to a human-readable format
  const formattedStakedAmount = stakedAmount
    ? formatUnits(stakedAmount, 18)
    : 0;
  const displayStakedAmount = formatNumber(formattedStakedAmount);

  const { data: allowance } = useReadContract({
    address: pepecoinCA,
    abi: pepecoinABI,
    functionName: "allowance",
    args: [account.address, brainCA],
    query: {
      refetchInterval: 3000,
    },
  });

  const formattedAllowance = allowance ? Number(formatUnits(allowance, 18)) : 0;
  const isAllowed = formattedAllowance >= Number(pepecoinToStake);

  const [isVisible, setIsVisible] = useState(false);
  const toggleVisibility = () => setIsVisible(!isVisible);
  const isMobile = window.innerWidth <= 575.98;

  function approvePepecoin() {
    writeApprovalContract({
      address: pepecoinCA,
      abi: pepecoinABI,
      functionName: "approve",
      args: [brainCA, pepecoinToStakeUint256],
    });
  }

  async function stakePepecoin(e) {
    e.preventDefault();

    if (!isAllowed) {
      console.log("Approval needed. Approving Pepecoin...");
      await approvePepecoin();
      console.log("Approval complete. Proceeding with staking...");
    } else {
      writeStakeContract({
        address: brainCA,
        abi: brain721Abi,
        functionName: "stakePepecoin",
        args: [pepecoinToStakeUint256],
      });
    }
  }

  const buttonDisabled =
    pepecoinToStake < 100000 ||
    pepecoinToStake > pepecoinBalance ||
    isApprovalPending ||
    isStakePending ||
    isApprovalConfirming ||
    isStakeConfirming;

  const getButtonText = () => {
    if (isApprovalPending || isApprovalConfirming) return "Approving...";
    if (isStakePending || isStakeConfirming) return "Staking...";
    if (pepecoinToStake < 100000) return "Minimum 1 Brain";
    if (pepecoinToStake > pepecoinBalance) return "Insufficient Balance";
    if (!isAllowed) return "Approve Pepecoin";
    return "Stake";
  };

  return (
    <>
      <ButtonHoverAnimation />
      <main data-barba="container" data-barba-namespace="secondary">
        <div className="min-svh-100 d-flex align-items-center justify-content-center px-3 px-sm-4 px-md-5  opacity-load">
          <div className="container w-100">
            <div className="spacer"></div>
            <div className="my-4">
              <h1 className="text-center">Stake Pepecoin</h1>
              <div className="mt-4 mt-lg-5">
                <div className="box mx-auto">
                  <div className="box-head py-3 py-md-4 px-3 px-md-5 d-flex justify-content-between align-items-end">
                    <div>
                      <small className="font-mono text-uppercase d-block mb-2 d-flex align-items-center">
                        <span className="muted">Pepecoin Available</span>{" "}
                      </small>
                      <p className="fs-3 mb-0 lh-1 text-uppercase">
                        <PepecoinBalance />
                      </p>
                    </div>
                    <div className="text-end">
                      <em>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="21"
                          height="20"
                          viewBox="0 0 21 20"
                          fill="none"
                          className="position-relative"
                          style={{ top: "-2px" }}
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10.0605 0H16.0605C17.7392 0 19.1763 1.03408 19.7697 2.5H6.35123C6.94462 1.03408 8.38178 0 10.0605 0ZM0.0604858 10V14H4.06049C5.16506 14 6.06049 13.1046 6.06049 12C6.06049 10.8954 5.16506 10 4.06049 10H0.0604858ZM20.0605 4H4.06049C1.85135 4 0.0604858 5.79086 0.0604858 8V8.5H4.06049C5.99348 8.5 7.56049 10.067 7.56049 12C7.56049 13.933 5.99348 15.5 4.06049 15.5H0.0604858V16C0.0604858 18.2091 1.85135 20 4.06049 20H16.0605C18.2696 20 20.0605 18.2091 20.0605 16V4Z"
                            fill="#75FBFD"
                          ></path>
                        </svg>
                      </em>
                      <span className="ps-1">{shortenedAddress}</span>
                      <div className="font-mono text-blue text-uppercase mb-1">
                        {displayStakedAmount} Staked
                      </div>
                    </div>
                  </div>
                  <hr className="my-0 border-green" />
                  <div className="pt-3 pt-md-4 px-3 px-md-5">
                    <form
                      action=""
                      className={isStakeConfirmed ? "submitted" : ""}
                    >
                      <div className="form">
                        <div className="row">
                          <div className="col-sm-8 mb-3">
                            <label
                              htmlFor="InputBrains"
                              className="form-label d-block small opacity-75 font-mono ps-3"
                            >
                              # of Brains
                            </label>
                            <div className="position-relative">
                              <div className="input-icon p-3">
                                <img src="img/icon-brains.png" alt="" />
                              </div>
                              <input
                                type="text"
                                className="form-control ps-6"
                                id="InputBrains"
                                value={
                                  Math.floor(pepecoinToStake / 100000) +
                                  " Brain(s)"
                                }
                                placeholder="NFT Brains"
                                disabled
                                style={{ cursor: "default" }}
                              />
                              <div className="input-text small opacity-75 font-mono text-uppercase p-3 d-flex align-items-center">
                                <button
                                  className="max-btn"
                                  type="button"
                                  onClick={() =>
                                    setPepecoinToStake(
                                      parseInt(pepecoinBalance)
                                    )
                                  }
                                >
                                  max
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="font-mono text-uppercase d-flex align-items-center col-sm-4 gap-2 mb-3">
                            <button
                              className="qty-btn"
                              type="button"
                              onClick={() =>
                                setPepecoinToStake(
                                  Math.max(0, pepecoinToStake - 100000)
                                )
                              }
                              disabled={pepecoinToStake < 100000}
                            >
                              -
                            </button>
                            <button
                              type="button"
                              className="qty-btn"
                              onClick={() =>
                                setPepecoinToStake(
                                  Math.min(
                                    pepecoinBalance,
                                    pepecoinToStake + 100000
                                  )
                                )
                              }
                              disabled={pepecoinToStake >= pepecoinBalance}
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={stakePepecoin}
                          // disabled={buttonDisabled}
                          disabled={true}
                        >
                          {/* {getButtonText()} */}
                          STAKING DISABLED
                        </button>
                        <div className="d-sm-flex justify-content-between mb-3 mt-3 mb-sm-2">
                          <small className="font-mono muted text-uppercase">
                            * Staking is temporarily disabled
                          </small>
                        </div>
                        <div className="pt-4">
                          <div className="d-sm-flex justify-content-between mb-3 mb-sm-2">
                            <small className="font-mono muted text-uppercase">
                              You are about to stake
                            </small>
                            <small className="font-mono text-uppercase d-block">
                              <animated.span>
                                {number.to((n) => {
                                  const num = parseFloat(n);
                                  return num.toFixed(0);
                                })}
                              </animated.span>{" "}
                              PEPECOIN
                              {/* {Math.floor(pepecoinToStake / 100000) * 100000 +
                                " PEPECOIN"} */}
                            </small>
                          </div>
                          <div className="d-sm-flex justify-content-between mb-3 mb-sm-2">
                            <small className="font-mono muted text-uppercase d-block">
                              Exchange rate
                            </small>
                            <small className="font-mono text-uppercase d-block">
                              100k pepecoin = 1 brain
                            </small>
                          </div>
                        </div>
                      </div>
                      <StakingSuccess
                        pepecoinStaked={pepecoinToStake}
                        brainsEarned={Math.floor(pepecoinToStake / 100000)}
                        hash={stakeHash}
                      />
                    </form>
                  </div>

                  <div className="pb-3 pb-md-4 px-3 px-md-5">
                    {isMobile && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: isVisible ? "auto" : 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="readMoreContent overflow-hidden">
                          <hr className="my-0 my-sm-4" />
                          <ul className="list-1">
                            <li>
                              Stake <strong>100,000 Pepecoin</strong> to earn
                              one (1) Brain NFT.
                            </li>
                            <li>
                              Lock-up period of <strong>90 days</strong>
                            </li>
                            <li>
                              Please read the relevant documentation for staking{" "}
                              <a
                                className="table-link"
                                href="https://docs.basedlabs.net/based-cli-documentation/cs8Tej4WRmZe0K7QqRFH"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                here
                              </a>
                              .
                            </li>
                          </ul>
                        </div>
                      </motion.div>
                    )}
                    {!isMobile && (
                      <div className="readMoreContent read-more-content-mobile">
                        <hr className="my-0 my-sm-4" />
                        <ul className="list-1">
                          <li>
                            Stake <strong>100,000 Pepecoin</strong> to earn one
                            (1) Brain NFT.
                          </li>
                          <li>
                            Lock-up period of <strong>90 days</strong>
                          </li>
                          <li>
                            Please read the relevant documentation for staking{" "}
                            <a
                              className="table-link"
                              href="https://docs.basedlabs.net/based-cli-documentation/cs8Tej4WRmZe0K7QqRFH"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              here
                            </a>
                            .
                          </li>
                        </ul>
                      </div>
                    )}
                    <div
                      className={`readMore d-sm-none ${
                        isVisible ? "open" : ""
                      }`}
                      onClick={toggleVisibility}
                    >
                      <strong>
                        <svg
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="12.0068"
                            cy="12.9766"
                            r="12"
                            transform="rotate(90 12.0068 12.9766)"
                            fill="#75FBFD"
                          />
                          <path
                            d="M15.249 11.8496L12.0073 14.1041L8.76562 11.8496"
                            stroke="black"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </strong>
                      <span className="ps-1">
                        {isVisible ? "Close" : "Read More"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <Link
                className="underline-on-hover-2 d-flex align-items-center mx-auto mt-4 justify-content-center"
                to="/acquire-brain"
              >
                <em>
                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="12.4526" cy="12.3848" r="12" fill="#75FBFD" />
                    <path
                      d="M13.7493 15.6265L11.1559 12.3848L13.7493 9.14306"
                      stroke="black"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </em>
                <span className="ps-2">Go Back</span>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Staking;
